/**
 * UI组件库入口文件
 * 提供统一的组件导出和组件管理功能
 */

import type { App } from 'vue'

// 基础组件
import Button from './Button/index.vue'
import Icon from './Icon/index.vue'
import Avatar from './Avatar/index.vue'
import Badge from './Badge/index.vue'
import Tag from './Tag/index.vue'
import Tooltip from './Tooltip/index.vue'
import Popover from './Popover/index.vue'
import Modal from './Modal/index.vue'
import Drawer from './Drawer/index.vue'
import Dropdown from './Dropdown/index.vue'
import Menu from './Menu/index.vue'
import Breadcrumb from './Breadcrumb/index.vue'
import Pagination from './Pagination/index.vue'
import Loading from './Loading/index.vue'
import Empty from './Empty/index.vue'
import Result from './Result/index.vue'

// 表单组件
import Input from './Input/index.vue'
import DhSearchForm from './DhSearchForm/index.vue'
import DhPageHeader from './DhPageHeader/index.vue'
import DhSelect from './DhSelect/index.vue'

// 主题组件
import ThemeToggle from './ThemeToggle/index.vue'

/**
 * 组件映射表
 */
export const COMPONENT_MAP = {
  // 基础组件
  Button,
  Icon,
  Avatar,
  Badge,
  Tag,
  Tooltip,
  Popover,
  Modal,
  Drawer,
  Dropdown,
  Menu,
  Breadcrumb,
  Pagination,
  Loading,
  Empty,
  Result,

  // 表单组件
  Input,
  DhSearchForm,
  DhPageHeader,
  DhSelect,

  // 主题组件
  ThemeToggle
} as const

/**
 * 组件配置接口
 */
export interface ComponentConfig {
  /** 组件名称 */
  name: string
  /** 组件别名 */
  alias?: string[]
  /** 是否全局注册 */
  global?: boolean
  /** 组件分类 */
  category?: string
  /** 组件描述 */
  description?: string
  /** 组件版本 */
  version?: string
  /** 组件依赖 */
  dependencies?: string[]
}

/**
 * 组件管理器类
 */
export class ComponentManager {
  private app: App | null = null
  private components = new Map<string, any>()
  private configs = new Map<string, ComponentConfig>()
  private aliases = new Map<string, string>()

  /**
   * 设置Vue应用实例
   */
  setApp(app: App): void {
    this.app = app
  }

  /**
   * 注册组件
   */
  registerComponent(
    name: string,
    component: any,
    config?: ComponentConfig
  ): void {
    this.components.set(name, component)

    if (config) {
      this.configs.set(name, { ...config, name })

      // 注册别名
      if (config.alias) {
        config.alias.forEach(alias => {
          this.aliases.set(alias, name)
        })
      }
    }

    // 如果有Vue应用实例且配置为全局注册，则注册为全局组件（避免重复）
    if (this.app && (!config || config.global !== false)) {
      const exists = this.app.component(name)
      if (!exists) {
        this.app.component(name, component)
      }
      if (config?.alias) {
        config.alias.forEach(alias => {
          const aliasExists = this.app!.component(alias)
          if (!aliasExists) {
            this.app!.component(alias, component)
          }
        })
      }
    }
  }

  /**
   * 批量注册组件
   */
  registerComponents(
    components: Record<string, any>,
    configs?: Record<string, ComponentConfig>
  ): void {
    Object.entries(components).forEach(([name, component]) => {
      const config = configs?.[name]
      this.registerComponent(name, component, config)
    })
  }

  /**
   * 获取组件
   */
  getComponent(name: string): any {
    // 先检查直接名称
    if (this.components.has(name)) {
      return this.components.get(name)
    }

    // 再检查别名
    const realName = this.aliases.get(name)
    if (realName) {
      return this.components.get(realName)
    }

    return null
  }

  /**
   * 获取组件配置
   */
  getComponentConfig(name: string): ComponentConfig | undefined {
    const realName = this.aliases.get(name) || name
    return this.configs.get(realName)
  }

  /**
   * 获取所有组件
   */
  getAllComponents(): Map<string, any> {
    return new Map(this.components)
  }

  /**
   * 获取组件列表
   */
  getComponentList(): string[] {
    return Array.from(this.components.keys())
  }

  /**
   * 按分类获取组件
   */
  getComponentsByCategory(category: string): Record<string, any> {
    const result: Record<string, any> = {}

    for (const [name, config] of this.configs) {
      if (config.category === category) {
        const component = this.components.get(name)
        if (component) {
          result[name] = component
        }
      }
    }

    return result
  }

  /**
   * 检查组件是否存在
   */
  hasComponent(name: string): boolean {
    return this.components.has(name) || this.aliases.has(name)
  }

  /**
   * 注销组件
   */
  unregisterComponent(name: string): void {
    this.components.delete(name)
    this.configs.delete(name)

    // 删除相关别名
    for (const [alias, realName] of this.aliases) {
      if (realName === name) {
        this.aliases.delete(alias)
      }
    }
  }

  /**
   * 清空所有组件
   */
  clear(): void {
    this.components.clear()
    this.configs.clear()
    this.aliases.clear()
  }
}

/**
 * 全局组件管理器实例
 */
export const componentManager = new ComponentManager()

/**
 * 组件工具函数
 */
export const componentUtils = {
  /**
   * 创建组件配置
   */
  createComponentConfig(
    name: string,
    options: Partial<Omit<ComponentConfig, 'name'>> = {}
  ): ComponentConfig {
    return {
      name,
      global: true,
      category: 'general',
      ...options
    }
  },

  /**
   * 获取组件分类列表
   */
  getCategories(): string[] {
    const categories = new Set<string>()

    for (const config of componentManager.configs.values()) {
      if (config.category) {
        categories.add(config.category)
      }
    }

    return Array.from(categories)
  },

  /**
   * 搜索组件
   */
  searchComponents(keyword: string): Record<string, any> {
    const result: Record<string, any> = {}
    const lowerKeyword = keyword.toLowerCase()

    for (const [name, config] of componentManager.configs) {
      const matchName = name.toLowerCase().includes(lowerKeyword)
      const matchDescription = config.description?.toLowerCase().includes(lowerKeyword)
      const matchAlias = config.alias?.some(alias =>
        alias.toLowerCase().includes(lowerKeyword)
      )

      if (matchName || matchDescription || matchAlias) {
        const component = componentManager.getComponent(name)
        if (component) {
          result[name] = component
        }
      }
    }

    return result
  }
}

/**
 * Vue插件安装函数
 */
export function install(app: App): void {
  if ((app.config.globalProperties as any).__componentsInstalled) {
    return
  }
  ;(app.config.globalProperties as any).__componentsInstalled = true
  // 设置应用实例
  componentManager.setApp(app)

  // 注册已创建的组件
  Object.entries(COMPONENT_MAP).forEach(([name, component]) => {
    componentManager.registerComponent(name, component, {
      name,
      global: true,
      category: 'ui'
    })
  })

  // 注册全局属性
  app.config.globalProperties.$components = componentManager
  app.config.globalProperties.$componentUtils = componentUtils

  // 提供依赖注入
  app.provide('componentManager', componentManager)
  app.provide('componentUtils', componentUtils)
}

/**
 * 设置全局组件
 * @param app Vue应用实例
 */
export function setupGlobalComponents(app: App): void {
  install(app)
}

// 导出所有组件
// 导出所有组件
export { default as Button } from './Button/index.vue'
export { default as Icon } from './Icon/index.vue'
export { default as Avatar } from './Avatar/index.vue'
export { default as Badge } from './Badge/index.vue'
export { default as Tag } from './Tag/index.vue'
export { default as Tooltip } from './Tooltip/index.vue'
export { default as Popover } from './Popover/index.vue'
export { default as Modal } from './Modal/index.vue'
export { default as Drawer } from './Drawer/index.vue'
export { default as Dropdown } from './Dropdown/index.vue'
export { default as Menu } from './Menu/index.vue'
export { default as Breadcrumb } from './Breadcrumb/index.vue'
export { default as Pagination } from './Pagination/index.vue'
export { default as Loading } from './Loading/index.vue'
export { default as Empty } from './Empty/index.vue'
export { default as Result } from './Result/index.vue'
export { default as Input } from './Input/index.vue'
export { default as ThemeToggle } from './ThemeToggle/index.vue'

// 导出类型
export type {
  ComponentConfig
}

// 默认导出
export default {
  install,
  componentManager,
  componentUtils
}
